'use client';
import { Header as HeaderSparky, LayoutContainer } from '@petsmart-ui/sparky';
import type { Entry } from 'contentful';
import type { PropsWithChildren } from 'react';

import { LegacyIcon, LegacyIconName } from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';

import { ComponentResolver } from '../../utils';
import { Autocomplete } from '../autocomplete';
import { LegacyAccountDropdown } from '../LegacyAccountDropdown';
import { LegacyHeaderDrawer } from '../LegacyHeaderDrawer';
import { LegacyMinicart } from '../LegacyMinicart';
import { LegacyNavAdoptions } from '../LegacyNavAdoptions';
import { LogoWrapper } from '../LogoWrapper';
import { Resolver } from '../resolver';
import LegacyHeaderPersonIcon from './LegacyHeaderPersonIcon';
import type { LegacyHeaderProps } from './types';
import './LegacyHeader.scss';

export default function LegacyHeader({
    entry,
    entries,
    children,
    isOptexSearchBiaGuestEnabled = false,
}: PropsWithChildren<LegacyHeaderProps>) {
    const { mainMenu, livesSaved, secondaryNavigation } = entry?.fields ?? {};

    return (
        <>
            <Resolver entries={entries} />
            <HeaderSparky className="legacy-header" data-testid="legacy-header">
                <HeaderSparky.Top className="legacy-header__section legacy-header__section--divide">
                    <LayoutContainer className="legacy-header__container">
                        <LegacyNavAdoptions
                            secondaryNavigation={secondaryNavigation as Entry}
                            livesSaved={livesSaved as Entry}
                        />
                    </LayoutContainer>
                </HeaderSparky.Top>
                <HeaderSparky.Middle className="legacy-header__section legacy-header__section" hideMenuButton>
                    <LayoutContainer className="legacy-header__container" role="banner">
                        <div className="legacy-header__banner--desktop">
                            <LegacyHeaderDrawer
                                secondaryNavigation={secondaryNavigation as Entry}
                                mainMenu={mainMenu as Entry}
                            />
                            <div className="legacy-header__logo">
                                <LogoWrapper />
                            </div>

                            <div className="legacy-header__search">
                                {/* Optimizely START : SB-4665 */}
                                <Autocomplete
                                    className="legacy-autocomplete"
                                    productsHitPerPage={2}
                                    isOptexSearchBiaGuestEnabled={isOptexSearchBiaGuestEnabled}
                                />
                                {/* Optimizely END : SB-4665 */}
                                <div className="ai-assist">{children}</div>
                            </div>

                            <div className="legacy-header__account">
                                <LegacyAccountDropdown />
                                <LegacyHeaderPersonIcon />
                            </div>
                            <div className="legacy-header__cart">
                                <a
                                    href={NAVIGATION_URLS.legacy.storeLocator.base}
                                    title="Locate Stores"
                                    className="legacy-header__cart-pin-icon"
                                >
                                    <LegacyIcon aria-hidden="true" name={LegacyIconName.PIN} isPriority size={24} />
                                </a>
                                <LegacyMinicart />
                            </div>
                        </div>
                    </LayoutContainer>
                </HeaderSparky.Middle>
                <HeaderSparky.Bottom
                    className="legacy-header__section legacy-header__section--primary"
                    data-testid="legacy-mainmenu"
                >
                    <LayoutContainer className="legacy-header__container">
                        <ComponentResolver entry={mainMenu as Entry} />
                    </LayoutContainer>
                </HeaderSparky.Bottom>
            </HeaderSparky>
        </>
    );
}
